/**
 * SFrame143 – Secure & Inventory Assets: Phase Overview
 * Category-based entry point with 6 tiles and 4 functional filters.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import { SETTLEMENT_PLANNER_LAYOUT, SETTLEMENT_PLANNER_PROGRESS } from '../settlement/settlementTheme';

type TileStatus = 'not-started' | 'in-progress' | 'complete';

interface CategoryTile {
  id: string;
  title: string;
  description: string;
  filter: string;
  route: string;
  stepIds: string[];
}

const TILES: CategoryTile[] = [
  {
    id: 'financial',
    title: 'Financial Accounts & Assets',
    description:
      'Review and document bank accounts, investments, retirement accounts, and insurance.',
    filter: 'Assets',
    route: '/sframe-146',
    stepIds: ['si-1'],
  },
  {
    id: 'real-estate',
    title: 'Real Estate & Vehicles',
    description:
      'Document properties and vehicles, including ownership, location, and loan status.',
    filter: 'Assets',
    route: '/sframe-155',
    stepIds: ['si-4'],
  },
  {
    id: 'personal',
    title: 'Personal Belongings & Digital Assets',
    description: 'Capture personal items, valuables, and digital accounts.',
    filter: 'Assets',
    route: '/sframe-160',
    stepIds: ['si-5'],
  },
  {
    id: 'debts',
    title: 'Debts & Liabilities',
    description: 'Track outstanding debts, balances, and creditor obligations.',
    filter: 'Liabilities',
    route: '/sframe-150',
    stepIds: ['si-3'],
  },
  {
    id: 'legal',
    title: 'Legal and Important Documents',
    description:
      'Locate and organize legal documents and important records needed to manage and settle the estate.',
    filter: 'Documents',
    route: '/sframe-162',
    stepIds: ['si-6'],
  },
  {
    id: 'pets',
    title: 'Pets & Dependents',
    description: 'Review care plans, contacts, and instructions for dependents.',
    filter: 'Care',
    route: '/sframe-163',
    stepIds: ['si-7'],
  },
];

const FILTERS = ['All', 'Assets', 'Liabilities', 'Documents', 'Care'] as const;
type FilterValue = (typeof FILTERS)[number];

function deriveTileStatus(
  stepIds: string[],
  progressMap: Record<string, string>
): TileStatus {
  const statuses = stepIds.map((id) => progressMap[id] ?? 'not-started');
  if (statuses.every((s) => s === 'complete')) return 'complete';
  if (statuses.some((s) => s === 'in-progress' || s === 'complete')) return 'in-progress';
  return 'not-started';
}

function statusBadgeClass(status: TileStatus) {
  switch (status) {
    case 'in-progress':
      return 'border-amber-300 bg-amber-50 text-amber-900';
    case 'complete':
      return 'border-emerald-300 bg-emerald-50 text-emerald-900';
    default:
      return 'border-stone-200 bg-stone-100 text-stone-600';
  }
}

function statusLabel(status: TileStatus) {
  switch (status) {
    case 'in-progress':
      return 'In Progress';
    case 'complete':
      return 'Complete';
    default:
      return 'Not Started';
  }
}

function CheckIcon() {
  return (
    <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg className="w-5 h-5 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
    </svg>
  );
}

export default function SFrame143() {
  const { estateId } = useParams<{ estateId: string }>();
  const navigate = useNavigate();
  const { loadPhaseProgress, error } = useSettlementSession(estateId ?? null);

  const [progressMap, setProgressMap] = useState<Record<string, string>>({});
  const [activeFilter, setActiveFilter] = useState<FilterValue>('All');

  useEffect(() => {
    if (!estateId) return;
    let cancelled = false;
    (async () => {
      const progressData = await loadPhaseProgress('secure-inventory');
      if (cancelled) return;
      const map: Record<string, string> = {};
      for (const p of progressData) {
        map[p.stepId] = p.status;
      }
      setProgressMap(map);
    })();
    return () => {
      cancelled = true;
    };
  }, [estateId, loadPhaseProgress]);

  const tileStatuses = useMemo(
    () =>
      TILES.reduce<Record<string, TileStatus>>((acc, tile) => {
        acc[tile.id] = deriveTileStatus(tile.stepIds, progressMap);
        return acc;
      }, {}),
    [progressMap]
  );

  const completedCount = useMemo(
    () => Object.values(tileStatuses).filter((s) => s === 'complete').length,
    [tileStatuses]
  );

  const visibleTiles = useMemo(
    () => (activeFilter === 'All' ? TILES : TILES.filter((t) => t.filter === activeFilter)),
    [activeFilter]
  );

  const handleFilterClick = (filter: FilterValue) => {
    setActiveFilter((prev) => (prev === filter ? 'All' : filter));
  };

  const handleTileClick = (route: string) => {
    navigate(estateId ? `${route}/${estateId}` : route);
  };

  const progressPercent = Math.round((completedCount / TILES.length) * 100);

  return (
    <>
      {error && (
        <div className={`${SETTLEMENT_PLANNER_LAYOUT.container} mb-4`}>
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">{error}</div>
        </div>
      )}

      <div className={SETTLEMENT_PLANNER_LAYOUT.container}>
        <div className={SETTLEMENT_PLANNER_LAYOUT.contentColumn}>

          {/* Page header */}
          <div className="mb-5">
            <h1 className={SETTLEMENT_PLANNER_LAYOUT.title}>
              Secure &amp; Inventory Assets
            </h1>
            <p className={`${SETTLEMENT_PLANNER_LAYOUT.subtitle} mt-2`}>
              Identify, review, and organize all assets, accounts, and obligations in the estate.
            </p>
          </div>

          {/* Progress card */}
          <div className={`${SETTLEMENT_PLANNER_PROGRESS.card} mb-6`}>
            <div className="flex items-center justify-between mb-1">
              <p className={`text-base ${SETTLEMENT_PLANNER_PROGRESS.progressTitle}`}>Progress</p>
              <span className={SETTLEMENT_PLANNER_PROGRESS.percent}>{progressPercent}%</span>
            </div>
            <span className={`${SETTLEMENT_PLANNER_PROGRESS.count} block mb-2`}>
              {completedCount} of {TILES.length} categories completed
            </span>
            <div className={SETTLEMENT_PLANNER_PROGRESS.track}>
              <div
                className={SETTLEMENT_PLANNER_PROGRESS.fill}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            {FILTERS.map((filter) => (
              <button
                key={filter}
                type="button"
                onClick={() => handleFilterClick(filter)}
                className={
                  activeFilter === filter
                    ? SETTLEMENT_PLANNER_LAYOUT.categoryFilterActive
                    : SETTLEMENT_PLANNER_LAYOUT.categoryFilterInactive
                }
              >
                {filter}
              </button>
            ))}
          </div>

          {/* Supporting copy */}
          <p className="text-sm text-stone-500 mb-6">
            Work through each category to confirm what exists and add anything missing.
          </p>

          {/* Category tiles */}
          <div className="space-y-3">
            {visibleTiles.map((tile) => {
              const status = tileStatuses[tile.id] ?? 'not-started';
              return (
                <button
                  key={tile.id}
                  type="button"
                  onClick={() => handleTileClick(tile.route)}
                  className="w-full text-left border border-neutral-200 rounded-lg p-6 bg-white hover:border-cyan-500 transition-colors group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {status === 'complete' && <CheckIcon />}
                        <h2 className="text-base font-medium text-stone-800 leading-tight group-hover:text-cyan-900 transition-colors">
                          {tile.title}
                        </h2>
                      </div>
                      <p className="text-sm text-stone-700 mt-1 leading-relaxed">{tile.description}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 pt-0.5">
                      <span
                        className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${statusBadgeClass(status)}`}
                      >
                        {statusLabel(status)}
                      </span>
                      <ChevronRightIcon />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

        </div>
      </div>
    </>
  );
}
